// src/services/openFoodFactsService.js - Version améliorée avec calcul CG depuis étiquette
import { OPEN_FOOD_FACTS_API } from '../utils/constants';
import { calculateGL, getCategory } from '../utils/calculations';

class OpenFoodFactsService {
  /**
   * Search product by barcode
   * @param {string} barcode - Product barcode
   * @returns {Object|null} Product data or null if not found
   */
  async searchByBarcode(barcode) {
    if (!barcode) {
      throw new Error('Barcode is required');
    }

    // Nettoyer le code-barres (sécurité supplémentaire)
    const cleanBarcode = barcode.trim();

    // Log de debug (facilite le dépannage)
    console.log('🔍 Recherche Open Food Facts:', {
      barcode: cleanBarcode,
      length: cleanBarcode.length,
      url: `${OPEN_FOOD_FACTS_API.PRODUCT_URL}/${cleanBarcode}.json`
    });

    try {
      const response = await fetch(`${OPEN_FOOD_FACTS_API.PRODUCT_URL}/${cleanBarcode}.json`);
      const data = await response.json();

      if (data.status === 1 && data.product) {
        console.log('✅ Produit trouvé:', data.product.product_name || 'Sans nom');
        return this.formatProductData(data.product, cleanBarcode);
      }

      console.warn('⚠️ Produit non trouvé dans Open Food Facts:', cleanBarcode);
      return null;
    } catch (error) {
      console.error('❌ Erreur API Open Food Facts:', error);
      throw new Error('Erreur lors de la recherche du produit');
    }
  }

  /**
   * Search products by name
   * @param {string} productName - Product name to search
   * @param {number} maxResults - Maximum results to return
   * @returns {Array} Array of product data
   */
  async searchByName(productName, maxResults = OPEN_FOOD_FACTS_API.MAX_RESULTS) {
    if (!productName) {
      throw new Error('Product name is required');
    }

    try {
      const searchUrl = `${OPEN_FOOD_FACTS_API.SEARCH_URL}?search_terms=${encodeURIComponent(productName)}&json=1&page_size=${OPEN_FOOD_FACTS_API.DEFAULT_PAGE_SIZE}`;
      const response = await fetch(searchUrl);
      const data = await response.json();
      
      if (data.products && data.products.length > 0) {
        return data.products
          .slice(0, maxResults)
          .map((product, index) => this.formatProductData(product, product.code, `temp_${index}`));
      }
      
      return [];
    } catch (error) {
      console.error('Error searching by name:', error);
      throw new Error('Erreur lors de la recherche du produit');
    }
  }

  /**
   * Parse nutrition data from Open Food Facts product
   * @param {Object} product - Product data from OFF
   * @returns {Object} Parsed nutrition data
   */
  parseNutritionData(product) {
    const nutriments = product.nutriments || {};
    return {
      // Glucides - données de base pour CG
      carbohydrates: nutriments.carbohydrates_100g || 0,
      sugars: nutriments.sugars_100g || 0,
      fiber: nutriments.fiber_100g || 0,
      
      // Autres macronutriments qui influencent l'IG
      proteins: nutriments.proteins_100g || 0,
      fat: nutriments.fat_100g || 0,
      saturatedFat: nutriments['saturated-fat_100g'] || 0,
      
      // Données complémentaires
      energy: nutriments.energy_100g || 0,
      salt: nutriments.salt_100g || 0,
      sodium: nutriments.sodium_100g || 0,
      
      // Vitamines et minéraux (optionnel)
      calcium: nutriments.calcium_100g || 0,
      iron: nutriments.iron_100g || 0,
      vitaminC: nutriments['vitamin-c_100g'] || 0
    };
  }

  /**
   * Estimate GI from nutrition label data
   * Méthode améliorée basée sur la recherche nutritionnelle
   * @param {Object} nutrition - Nutrition data
   * @param {Array} categories - Product categories
   * @returns {Object} Estimation result with GI and confidence
   */
  estimateGIFromNutrition(nutrition, categories = []) {
    let baseGI = 55; // IG moyen par défaut
    let confidence = 'faible'; // Niveau de confiance
    let factors = [];

    // 1. Estimation basée sur les catégories d'aliment (plus fiable)
    const categoryGI = this.getGIFromCategories(categories);
    if (categoryGI.found) {
      baseGI = categoryGI.gi;
      confidence = 'moyenne';
      factors.push(`Catégorie: ${categoryGI.category}`);
    }

    // 2. Ajustements basés sur la composition nutritionnelle
    const adjustments = this.calculateNutritionalAdjustments(nutrition);
    baseGI += adjustments.total;
    factors = [...factors, ...adjustments.factors];

    // 3. Calcul de la confiance basé sur la disponibilité des données
    const dataQuality = this.assessDataQuality(nutrition, categories);
    if (dataQuality.score >= 0.8) {
      confidence = 'élevée';
    } else if (dataQuality.score >= 0.6) {
      confidence = 'moyenne';
    }

    // Limiter l'IG entre 15 et 100
    const finalGI = Math.max(15, Math.min(100, Math.round(baseGI)));

    return {
      gi: finalGI,
      confidence,
      factors,
      method: 'estimation_nutritionnelle',
      dataQuality: dataQuality.score,
      warning: confidence === 'faible' ? 'Estimation approximative - données limitées' : null
    };
  }

  /**
   * Get GI estimation from product categories
   * @param {Array} categories - Product categories
   * @returns {Object} Category-based GI estimation
   */
  getGIFromCategories(categories = []) {
    if (!categories.length) return { found: false };

    const categoryString = categories.join(' ').toLowerCase();
    
    // Ordre de priorité : du plus spécifique au plus général
    const categoryMappings = [
      // Légumes et fruits (IG généralement bas)
      { keywords: ['vegetable', 'legume', 'green', 'salad'], gi: 25, category: 'légumes' },
      { keywords: ['fruit', 'apple', 'orange', 'berry'], gi: 40, category: 'fruits' },
      
      // Légumineuses (IG bas)
      { keywords: ['bean', 'lentil', 'chickpea', 'leguminous'], gi: 30, category: 'légumineuses' },
      
      // Produits laitiers (IG généralement bas)
      { keywords: ['dairy', 'milk', 'yogurt', 'cheese'], gi: 30, category: 'produits laitiers' },
      
      // Céréales et féculents
      { keywords: ['pasta', 'noodle'], gi: 45, category: 'pâtes' },
      { keywords: ['bread', 'baguette'], gi: 70, category: 'pain' },
      { keywords: ['rice', 'riz'], gi: 70, category: 'riz' },
      { keywords: ['potato', 'pomme-de-terre'], gi: 75, category: 'pommes de terre' },
      { keywords: ['cereal', 'flake'], gi: 60, category: 'céréales' },
      
      // Sucré
      { keywords: ['chocolate', 'candy', 'sweet'], gi: 65, category: 'produits sucrés' },
      { keywords: ['sugar', 'sucre'], gi: 68, category: 'sucre' },
      { keywords: ['jam', 'confiture'], gi: 65, category: 'confiture' },
      
      // Boissons
      { keywords: ['beverage', 'drink', 'juice'], gi: 50, category: 'boissons' },
      { keywords: ['soda', 'cola'], gi: 65, category: 'sodas' }
    ];

    for (const mapping of categoryMappings) {
      for (const keyword of mapping.keywords) {
        if (categoryString.includes(keyword)) {
          return {
            found: true,
            gi: mapping.gi,
            category: mapping.category
          };
        }
      }
    }

    return { found: false };
  }

  /**
   * Calculate nutritional adjustments to base GI
   * @param {Object} nutrition - Nutrition data
   * @returns {Object} Adjustments with factors
   */
  calculateNutritionalAdjustments(nutrition) {
    let total = 0;
    const factors = [];

    // Facteur sucres simples
    if (nutrition.sugars > 0) {
      const sugarRatio = nutrition.sugars / Math.max(nutrition.carbohydrates, 1);
      if (sugarRatio > 0.5) {
        total += 15;
        factors.push('Riche en sucres simples (+15)');
      } else if (sugarRatio > 0.25) {
        total += 8;
        factors.push('Contient des sucres simples (+8)');
      }
    }

    // Facteur fibres (très important pour l'IG)
    if (nutrition.fiber > 0) {
      if (nutrition.fiber > 10) {
        total -= 15;
        factors.push('Très riche en fibres (-15)');
      } else if (nutrition.fiber > 5) {
        total -= 10;
        factors.push('Riche en fibres (-10)');
      } else if (nutrition.fiber > 2) {
        total -= 5;
        factors.push('Source de fibres (-5)');
      }
    }

    // Facteur protéines
    if (nutrition.proteins > 15) {
      total -= 8;
      factors.push('Riche en protéines (-8)');
    } else if (nutrition.proteins > 8) {
      total -= 5;
      factors.push('Source de protéines (-5)');
    }

    // Facteur lipides
    if (nutrition.fat > 15) {
      total -= 8;
      factors.push('Riche en lipides (-8)');
    } else if (nutrition.fat > 8) {
      total -= 5;
      factors.push('Source de lipides (-5)');
    }

    // Facteur glucides totaux vs net (glucides - fibres)
    const netCarbs = Math.max(0, nutrition.carbohydrates - nutrition.fiber);
    if (netCarbs < nutrition.carbohydrates * 0.7) {
      total -= 5;
      factors.push('Glucides nets réduits (-5)');
    }

    return { total, factors };
  }

  /**
   * Assess data quality for confidence calculation
   * @param {Object} nutrition - Nutrition data
   * @param {Array} categories - Product categories
   * @returns {Object} Quality assessment
   */
  assessDataQuality(nutrition, categories) {
    let score = 0;
    const maxScore = 10;

    // Présence des données nutritionnelles clés
    if (nutrition.carbohydrates > 0) score += 3;
    if (nutrition.sugars >= 0) score += 2;
    if (nutrition.fiber >= 0) score += 2;
    if (nutrition.proteins >= 0) score += 1;
    if (nutrition.fat >= 0) score += 1;

    // Catégories disponibles
    if (categories && categories.length > 0) score += 1;

    return {
      score: score / maxScore,
      details: {
        hasCarbs: nutrition.carbohydrates > 0,
        hasFiber: nutrition.fiber >= 0,
        hasSugars: nutrition.sugars >= 0,
        hasCategories: categories && categories.length > 0
      }
    };
  }

  /**
   * Calculate Charge Glycémique directement depuis l'étiquette
   * @param {Object} nutrition - Nutrition data
   * @param {number} portion - Portion size in grams
   * @returns {Object} CG calculation result
   */
  calculateCGFromLabel(nutrition, portion = 100) {
    // Méthode 1: Glucides nets (recommandée pour diabétiques)
    const netCarbs = Math.max(0, nutrition.carbohydrates - nutrition.fiber);
    
    // Méthode 2: Tous les glucides
    const totalCarbs = nutrition.carbohydrates;
    
    // Estimation IG
    const giEstimation = this.estimateGIFromNutrition(nutrition, []);
    
    // Calculs CG
    const cgNet = calculateGL(giEstimation.gi, netCarbs, portion);
    const cgTotal = calculateGL(giEstimation.gi, totalCarbs, portion);
    
    return {
      // Résultats principaux
      chargeGlycemique: cgNet, // CG avec glucides nets (recommandée)
      chargeGlycemiqueTotal: cgTotal, // CG avec tous les glucides
      
      // Détails du calcul
      details: {
        igEstime: giEstimation.gi,
        glucidesNets: netCarbs,
        glucidesTotaux: totalCarbs,
        fibres: nutrition.fiber,
        portion: portion,
        confiance: giEstimation.confidence
      },
      
      // Recommandations
      recommendation: this.getCGRecommendation(cgNet),
      
      // Avertissements
      warnings: this.getCGWarnings(nutrition, giEstimation)
    };
  }

  /**
   * Get CG-based recommendations
   * @param {number} cg - Charge glycémique
   * @returns {Object} Recommendations
   */
  getCGRecommendation(cg) {
    if (cg <= 5) {
      return {
        level: 'très_favorable',
        message: 'Impact glycémique très faible - Excellent choix',
        color: 'green'
      };
    } else if (cg <= 10) {
      return {
        level: 'favorable',
        message: 'Impact glycémique faible - Bon choix',
        color: 'yellow'
      };
    } else if (cg <= 20) {
      return {
        level: 'modere',
        message: 'Impact glycémique modéré - À consommer avec modération',
        color: 'orange'
      };
    } else {
      return {
        level: 'eleve',
        message: 'Impact glycémique élevé - Limiter les portions',
        color: 'red'
      };
    }
  }

  /**
   * Get warnings about CG calculation
   * @param {Object} nutrition - Nutrition data
   * @param {Object} giEstimation - GI estimation result
   * @returns {Array} Array of warnings
   */
  getCGWarnings(nutrition, giEstimation) {
    const warnings = [];

    if (giEstimation.confidence === 'faible') {
      warnings.push({
        type: 'precision',
        message: 'L\'IG est estimé avec peu de données - résultat approximatif'
      });
    }

    if (nutrition.carbohydrates === 0) {
      warnings.push({
        type: 'no_carbs',
        message: 'Aucun glucide détecté - CG non applicable'
      });
    }

    if (nutrition.carbohydrates > 0 && nutrition.fiber === 0) {
      warnings.push({
        type: 'no_fiber_data',
        message: 'Données fibres manquantes - CG calculée avec tous les glucides'
      });
    }

    return warnings;
  }

  /**
   * Format product data from Open Food Facts with enhanced CG calculation
   * @param {Object} product - Raw product data from OFF
   * @param {string} barcode - Product barcode
   * @param {string|number} tempId - Temporary ID for search results
   * @returns {Object} Formatted product data
   */
  formatProductData(product, barcode, tempId = null) {
    const nutrition = this.parseNutritionData(product);
    const categories = product.categories_tags || [];
    
    // Calcul IG estimé
    const giEstimation = this.estimateGIFromNutrition(nutrition, categories);
    
    // Calcul CG depuis l'étiquette
    const cgCalculation = this.calculateCGFromLabel(nutrition, 100);
    
    const category = getCategory(cgCalculation.chargeGlycemique);

    return {
      id: tempId || Date.now(),
      name: product.product_name || `Produit ${barcode}`,
      gi: giEstimation.gi,
      carbs: nutrition.carbohydrates,
      gl: cgCalculation.chargeGlycemique,
      category,
      source: 'openfoodfacts',
      barcode: barcode,
      brand: product.brands || '',
      nutriscore: product.nutrition_grades || '',
      image: product.image_front_small_url || '',
      categories: product.categories || '',
      
      // Données étendues pour l'analyse
      nutrition: nutrition,
      giEstimation: {
        confidence: giEstimation.confidence,
        method: giEstimation.method,
        factors: giEstimation.factors,
        warning: giEstimation.warning
      },
      cgCalculation: {
        chargeGlycemique: cgCalculation.chargeGlycemique,
        chargeGlycemiqueTotal: cgCalculation.chargeGlycemiqueTotal,
        glucidesNets: cgCalculation.details.glucidesNets,
        glucidesTotaux: cgCalculation.details.glucidesTotaux,
        fibres: cgCalculation.details.fibres,
        recommendation: cgCalculation.recommendation,
        warnings: cgCalculation.warnings
      }
    };
  }

  /**
   * Get detailed analysis of a product's glycemic impact
   * @param {Object} product - Product data
   * @returns {Object} Detailed analysis
   */
  getDetailedGlycemicAnalysis(product) {
    if (!product.nutrition) {
      return { error: 'Données nutritionnelles manquantes' };
    }

    const analysis = {
      produit: {
        nom: product.name,
        marque: product.brand,
        codeBarres: product.barcode
      },
      
      composition: {
        glucidesTotaux: product.nutrition.carbohydrates,
        glucidesNets: Math.max(0, product.nutrition.carbohydrates - product.nutrition.fiber),
        sucres: product.nutrition.sugars,
        fibres: product.nutrition.fiber,
        proteines: product.nutrition.proteins,
        lipides: product.nutrition.fat
      },
      
      indiceGlycemique: {
        valeur: product.gi,
        confiance: product.giEstimation?.confidence || 'inconnue',
        methode: 'Estimation basée sur la composition',
        facteurs: product.giEstimation?.factors || []
      },
      
      chargeGlycemique: {
        pour100g: product.cgCalculation?.chargeGlycemique || product.gl,
        recommendation: product.cgCalculation?.recommendation,
        avertissements: product.cgCalculation?.warnings || []
      },
      
      conseils: this.getGlycemicAdvice(product),
      
      portions: this.calculatePortionSizes(product)
    };

    return analysis;
  }

  /**
   * Get advice based on glycemic analysis
   * @param {Object} product - Product data
   * @returns {Array} Array of advice
   */
  getGlycemicAdvice(product) {
    const advice = [];
    const cg = product.cgCalculation?.chargeGlycemique || product.gl;
    const nutrition = product.nutrition;

    // Conseils basés sur la CG
    if (cg <= 5) {
      advice.push({
        type: 'positive',
        message: 'Excellent choix ! Impact glycémique très faible.',
        icon: '✅'
      });
    } else if (cg <= 10) {
      advice.push({
        type: 'positive',
        message: 'Bon choix avec un impact glycémique modéré.',
        icon: '👍'
      });
    } else if (cg <= 20) {
      advice.push({
        type: 'caution',
        message: 'Impact modéré. Associez avec des fibres ou des protéines.',
        icon: '⚠️'
      });
    } else {
      advice.push({
        type: 'warning',
        message: 'Impact élevé. Limitez la portion ou évitez si diabétique.',
        icon: '🔴'
      });
    }

    // Conseils basés sur les fibres
    if (nutrition.fiber > 5) {
      advice.push({
        type: 'positive',
        message: 'Riche en fibres, aide à réduire l\'impact glycémique.',
        icon: '🌾'
      });
    } else if (nutrition.carbohydrates > 10 && nutrition.fiber < 2) {
      advice.push({
        type: 'suggestion',
        message: 'Faible en fibres. Ajoutez des légumes ou des fruits.',
        icon: '🥬'
      });
    }

    // Conseils basés sur les sucres
    if (nutrition.sugars > 0) {
      const sugarRatio = nutrition.sugars / Math.max(nutrition.carbohydrates, 1);
      if (sugarRatio > 0.5) {
        advice.push({
          type: 'warning',
          message: 'Très riche en sucres simples - pic glycémique rapide.',
          icon: '🍯'
        });
      }
    }

    // Conseils pour les protéines
    if (nutrition.proteins > 10) {
      advice.push({
        type: 'positive',
        message: 'Les protéines aident à stabiliser la glycémie.',
        icon: '💪'
      });
    }

    return advice;
  }

  /**
   * Calculate recommended portion sizes
   * @param {Object} product - Product data
   * @returns {Object} Portion recommendations
   */
  calculatePortionSizes(product) {
    // Calcul des portions pour différents objectifs CG
    const portions = {
      diabetique: this.calculatePortionForTargetCG(product, 5), // CG ≤ 5
      normal: this.calculatePortionForTargetCG(product, 10),    // CG ≤ 10
      sport: this.calculatePortionForTargetCG(product, 15)      // CG ≤ 15
    };

    return {
      portions,
      recommendations: {
        diabetique: `Pour un impact très faible : ${portions.diabetique}g maximum`,
        normal: `Pour un impact faible : ${portions.normal}g maximum`,
        sport: `Pour l'activité physique : ${portions.sport}g maximum`
      }
    };
  }

  /**
   * Calculate portion size for target CG
   * @param {Object} product - Product data
   * @param {number} targetCG - Target charge glycémique
   * @returns {number} Recommended portion in grams
   */
  calculatePortionForTargetCG(product, targetCG) {
    const productCG = product.cgCalculation?.chargeGlycemique || product.gl;
    
    if (productCG <= 0) return 100; // Pas de glucides
    
    const ratio = targetCG / productCG;
    const recommendedPortion = Math.round(100 * ratio);
    
    // Limites pratiques
    return Math.max(10, Math.min(500, recommendedPortion));
  }

  /**
   * Check if product data is complete enough for our app
   * @param {Object} product - Product data
   * @returns {boolean} True if product has enough data
   */
  isProductDataComplete(product) {
    return !!(
      product.product_name &&
      product.nutriments &&
      (product.nutriments.carbohydrates_100g !== undefined)
    );
  }

  /**
   * Get product quality score based on available data
   * @param {Object} product - Product data
   * @returns {number} Quality score from 0 to 10
   */
  getProductQualityScore(product) {
    let score = 0;
    
    // Basic info
    if (product.product_name) score += 2;
    if (product.brands) score += 1;
    if (product.categories) score += 1;
    
    // Nutrition data
    if (product.nutriments?.carbohydrates_100g !== undefined) score += 2;
    if (product.nutriments?.sugars_100g !== undefined) score += 1;
    if (product.nutriments?.fiber_100g !== undefined) score += 1;
    if (product.nutriments?.proteins_100g !== undefined) score += 1;
    if (product.nutriments?.fat_100g !== undefined) score += 1;
    
    return Math.min(score, 10);
  }

  /**
   * Export detailed nutrition analysis as text
   * @param {Object} product - Product data
   * @returns {string} Formatted analysis
   */
  exportNutritionAnalysis(product) {
    const analysis = this.getDetailedGlycemicAnalysis(product);
    
    let report = `ANALYSE GLYCÉMIQUE - ${analysis.produit.nom}\n`;
    report += `=====================================\n\n`;
    
    report += `COMPOSITION NUTRITIONNELLE (pour 100g):\n`;
    report += `- Glucides totaux: ${analysis.composition.glucidesTotaux}g\n`;
    report += `- Glucides nets: ${analysis.composition.glucidesNets}g\n`;
    report += `- Sucres: ${analysis.composition.sucres}g\n`;
    report += `- Fibres: ${analysis.composition.fibres}g\n`;
    report += `- Protéines: ${analysis.composition.proteines}g\n`;
    report += `- Lipides: ${analysis.composition.lipides}g\n\n`;
    
    report += `IMPACT GLYCÉMIQUE:\n`;
    report += `- Indice Glycémique estimé: ${analysis.indiceGlycemique.valeur}\n`;
    report += `- Charge Glycémique: ${analysis.chargeGlycemique.pour100g}\n`;
    report += `- Catégorie: ${analysis.chargeGlycemique.recommendation?.level}\n\n`;
    
    report += `CONSEILS:\n`;
    analysis.conseils.forEach(conseil => {
      report += `${conseil.icon} ${conseil.message}\n`;
    });
    
    report += `\nPORTIONS RECOMMANDÉES:\n`;
    Object.entries(analysis.portions.recommendations).forEach(([type, rec]) => {
      report += `- ${type}: ${rec}\n`;
    });
    
    report += `\n⚠️ IMPORTANT: Cette analyse est basée sur une estimation.\n`;
    report += `Pour un suivi médical précis, consultez un professionnel de santé.\n`;
    
    return report;
  }
}

// Export singleton instance
export const openFoodFactsService = new OpenFoodFactsService();
export default openFoodFactsService;